import { useMemo } from "react"
import { Receipt } from "lucide-react"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { Card } from "../../ui"
import { formatVND } from "../../constants"
import { HScroll } from "procurement/design/components/HScroll"

const Def = ({ label, children, full }) => (
  <div className={full ? "pc-def-full" : undefined}>
    <div className="pc-def-label">{label}</div>
    <div className="pc-def-value">{children ?? "—"}</div>
  </div>
)

const lineAmount = (l) => l.amount ?? (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)

// Non-PO (direct spend) payment: the hidden order + PO are shown as a
// reference only — they can't be opened or linked to other payments.
const NonPoDetailCard = ({ directOrder }) => {
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerName = useMemo(() => {
    const map = new Map((centersPage?.items || []).map((c) => [String(c.centerId), c.centerName]))
    return (id) => (id == null ? null : map.get(String(id)) || `Center #${id}`)
  }, [centersPage])

  if (!directOrder) return null
  const lines = directOrder.lines || []

  return (
    <Card icon={<Receipt aria-hidden />} title="Non-PO payment details" subtitle="Not linked to any purchase order.">
      <div className="pc-defgrid">
        <Def label="Title">{directOrder.title}</Def>
        <Def label="Budget category">{directOrder.budgetCategoryName}</Def>
        <Def label="Region">{directOrder.region}</Def>
        <Def label="Center">{centerName(directOrder.centerId)}</Def>
        <Def label="Order / Purchase">
          {[
            directOrder.orderCode && `${directOrder.orderCode} (${directOrder.orderStatus || "—"})`,
            directOrder.poCode && `${directOrder.poCode} (${directOrder.purchaseStatus || "—"})`,
          ]
            .filter(Boolean)
            .join(" → ") || null}
        </Def>
        {directOrder.reason && (
          <Def label="Reason" full>
            {directOrder.reason}
          </Def>
        )}
      </div>
      {lines.length > 0 && (
        <HScroll className="pc-table-wrap" style={{ marginTop: 16 }}>
          <table className="pc-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Center</th>
                <th className="pc-th-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l.id ?? idx}>
                  <td>
                    <div className="pc-td-strong">{l.itemName || l.offCatalogItemName || "—"}</div>
                    {!l.itemId && (l.offCatalogUnit || l.offCatalogDescription) && (
                      <div className="pc-muted" style={{ fontSize: 12 }}>
                        {[l.offCatalogUnit, l.offCatalogDescription].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td>{centerName(l.centerId) || "—"}</td>
                  <td className="pc-td-right">{formatVND(lineAmount(l))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </HScroll>
      )}
    </Card>
  )
}

export default NonPoDetailCard
